/**
 * @file 定时器/计时器相关的 hooks
 */
import { useTimeout as useTimeoutOrigin, useInterval as useIntervalOrigin, } from 'ahooks';
import { useEffect, useState, useCallback } from 'react';
/**
 * 一个可以处理 setTimeout 的 Hook
 * 详见: https://ahooks.js.org/zh-CN/hooks/use-timeout
 *
 * @category Timer Hook
 */
export const useTimeout = useTimeoutOrigin;
/**
 * 一个可以处理 setInterval 的 Hook
 * 详见: https://ahooks.js.org/zh-CN/hooks/use-interval
 *
 * @category Timer Hook
 */
export const useInterval = useIntervalOrigin;
/**
 * 设置 setInterval
 *
 * @category Timer Hook
 * @param args - Argument Object
 * @param args.interval - 间隔
 * @param args.callback - 回调函数
 * @param args.disabled - true 时会清理并暂停定时器
 */
export const useSetInterval = ({ interval, callback, disabled, }) => {
    useEffect(() => {
        if (!disabled) {
            const t = setInterval(callback, interval);
            return () => {
                clearInterval(t);
            };
        }
        return undefined;
    }, [interval, callback, disabled]);
};
/**
 * 设置一个以秒为单位的倒计时。
 *
 * @category Timer Hook
 * @param args - Argument object
 * @param args.seconds - 倒计时总秒数
 * @param args.accuracy - 毫秒数，用于指定倒计时更新时间间隔，影响计时精度，默认 200ms
 * @returns `{ seconds: 剩余秒数, isCounting: 是否正在倒计时, startCounting: 开始倒计时, stopCounting: 停止并重置倒计时 }`
 */
export const useCountDown = ({ seconds, accuracy = 200, }) => {
    const [started, setStarted] = useState(false);
    const [startAt, setStartAt] = useState();
    const [left, setLeft] = useState(seconds);
    useEffect(() => {
        if (started) {
            setStartAt(Date.now());
            setLeft(seconds);
        }
        else {
            setStartAt(undefined);
        }
    }, [seconds, started]);
    const callback = () => {
        if (started && startAt) {
            const now = Date.now();
            const l = Math.max(0, Math.round(seconds - (now - startAt) / 1000));
            setLeft(l);
            setStarted(!!l);
        }
    };
    useSetInterval({
        interval: accuracy,
        callback,
        disabled: !startAt,
    });
    const startCounting = useCallback(() => setStarted(true), []);
    const stopCounting = useCallback(() => setStarted(false), []);
    return { seconds: left, isCounting: started, startCounting, stopCounting };
};
/**
 * 以指定间隔刷新并返回当前时间的 hook
 *
 * @category Timer Hook
 * @param args - Argument object
 * @param args.interval - 刷新间隔。-1 表示不刷新
 * @returns Date 类型的当前时间
 */
export function useNow({ interval = 1000 } = {}) {
    const [now, setNow] = useState(() => new Date());
    useSetInterval({
        callback: () => {
            setNow(new Date());
        },
        interval,
        disabled: interval === -1,
    });
    return now;
}
//# sourceMappingURL=data:application/json;base64,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